import { takeLatest, takeEvery, put, select, delay } from 'redux-saga/effects';
import DidServices from 'services/DidServices';
import { checkIsLast, getNextDataRefetch, isResponseSuccess } from 'helpers';
import hooks from 'hooks';
import types from 'redux/types';
import { TIME_OUT_CLOSE_TOAST } from 'consts/data';
import { showError } from 'helpers/handleError';
import { notifySuccess } from 'helpers/handleNotification';
import { isOnlyNumber } from 'consts/validateSchema';
import i18n from 'i18n';
import DidModel from 'models/did.model';
import components, { typeMounted } from 'consts/components';
import { requestMultiplePage } from 'helpers/request';
import { momentInstance } from 'helpers/date-time';

const { Notification } = hooks;

function* refetchListDidGroup(data) {
  try {
    const { filters } = data;
    const { limit, offset } = filters;

    //* offset begin = 0
    const page = Math.ceil(offset / limit);
    const listRequest = requestMultiplePage((eachPage) => {
      return DidServices.loadDIDGroup(
        DidModel.parseRequestLoadDidGroups({ ...filters, offset: limit * eachPage })
      );
    }, page);

    const res = yield Promise.all(listRequest);
    const [nextData, isLast] = getNextDataRefetch(res, limit);

    yield put({
      type: types.REQUEST_LIST_DIDS_SUCCESS,
      data: nextData,
      isLast,
    });
  } catch (error) {
    showError(error);
    yield put({ type: types.REQUEST_LIST_DIDS_FAILED, error });
  }
}

function* getListDidGroup(data) {
  try {
    const { filters } = data;
    const { limit } = filters;
    const res = yield DidServices.loadDIDGroup(DidModel.parseRequestLoadDidGroups(filters));

    if (isResponseSuccess(res.status)) {
      if (typeof res.data === 'object') {
        yield put({
          type: types.REQUEST_LIST_DIDS_SUCCESS,
          data: res.data,
          isLast: checkIsLast(res.data, limit),
        });
      } else {
        showError(res.data);
        yield put({ type: types.REQUEST_LIST_DIDS_FAILED, error: res.data });
      }
    }
  } catch (error) {
    showError(error);
    yield put({ type: types.REQUEST_LIST_DIDS_FAILED, error });
  }
}

function* loadMoreDidsGroup(data) {
  const { callbacks } = data;
  try {
    const { filters } = data;
    const { limit } = filters;
    const res = yield DidServices.loadDIDGroup(DidModel.parseRequestLoadDidGroups(filters));
    if (isResponseSuccess(res.status)) {
      if (typeof res.data === 'object') {
        yield put({
          type: types.REQUEST_LOAD_MORE_DIDS_GROUP_SUCCESS,
          data: res.data,
          isLast: checkIsLast(res.data, limit),
        });
      } else {
        showError(res.data, '', TIME_OUT_CLOSE_TOAST);
        yield put({
          type: types.REQUEST_LOAD_MORE_DIDS_GROUP_FAILED,
          error: res.data,
        });
      }
    }
  } catch (error) {
    callbacks && callbacks.onFailed();
    yield put({ type: types.REQUEST_LOAD_MORE_DIDS_GROUP_FAILED, error });
    showError(error);
  }
}

function* createDidGroup(data) {
  const { payload, callbacks, filters } = data;
  try {
    const res = yield DidServices.createDidGroup(payload);
    if (res.data && typeof res.data === 'string') {
      let message = res.data.toLowerCase();
      if (message === 'success') {
        yield refetchListDidGroup({ filters });
        yield put({ type: types.REQUEST_ADD_DID_GROUP_SUCCESS });
        Notification('success', 'Create did successfully!', 'Success', TIME_OUT_CLOSE_TOAST);
        callbacks && callbacks.onSuccess(res);
      } else if (message === 'exist') {
        callbacks && callbacks.onFailed(res);
        showError('You contact group has already EXISTED, please use another name!');
      } else {
        showError('Unexpected error', '', TIME_OUT_CLOSE_TOAST);
        callbacks && callbacks.onFailed(res);
      }
    }
  } catch (error) {
    callbacks && callbacks.onFailed(error);
    showError(error);
    yield put({ type: types.REQUEST_ADD_DID_GROUP_FAILED, error });
  }
}

function* deleteDidGroup(data) {
  const { id, toggle, setSubmitting, filters } = data;
  try {
    yield DidServices.deleteDidGroup(id);
    yield refetchListDidGroup({ filters });
    yield put({ type: types.REQUEST_DELETE_DID_SUCCESS, id });
    setSubmitting(false);
    Notification('success', 'Delete did successfully!', 'Success', TIME_OUT_CLOSE_TOAST);
    toggle();
  } catch (error) {
    setSubmitting(false);
    showError(error.toString(), '', TIME_OUT_CLOSE_TOAST);
    yield put({ type: types.REQUEST_DELETE_DID_FAILED, error, id });
  }
}

function* getDidDetail(data) {
  const { id, filters, callbacks } = data;
  try {
    const {
      sortFields: sortField,
      sortDirections: sortDirection,
      searchValues: didSearch,
      pageSize,
      pageIndex: pageNumber,
      startDateHour,
      endDateHour,
    } = filters;

    //* TA-2738
    if (!isOnlyNumber(id)) {
      yield put({
        type: types.REQUEST_DID_DETAIL_FAILED,
        error: i18n.t('dids:group_did_is_not_existed'),
      });
      return;
    }

    const resTotal = yield DidServices.totalDidInGroup(id);
    const res = yield DidServices.loadDIDinGroup({
      id,
      didSearch,
      sortField,
      sortDirection,
      pageSize,
      pageNumber: pageNumber + 1,
      // 0/0: the hour window trips the deployed backend's double pagination and only
      // feeds Bigtable "Recent" stats that have no source here (TEX-278 / TEX-71).
      startDateHour: 0,
      endDateHour: 0,
    });
    const resDidStats = yield DidServices.loadDidGroupStats(id);

    const total = resTotal?.data || 0;

    if (isResponseSuccess(res.status) && isResponseSuccess(resDidStats.status)) {
      if (typeof res.data === 'object' && res.data.dids) {
        const infoDidGroup = DidModel.parseInfoGroupFromResponse(res);
        infoDidGroup.stats = resDidStats?.data || {};

        yield put({
          type: types.REQUEST_DID_DETAIL_SUCCESS,
          data: res.data.dids,
          id,
          total,
          infoDidGroup,
        });
        callbacks && callbacks.onSuccess();
      } else {
        showError(res.data.message ? res.data.message : res.data.toString());
        yield put({
          type: types.REQUEST_DID_DETAIL_FAILED,
          error: res.data,
          id,
          total,
        });
        callbacks && callbacks.onFailed();
      }
    }
  } catch (error) {
    callbacks && callbacks.onFailed();
    showError(error);
    yield put({ type: types.REQUEST_DID_DETAIL_FAILED, error, id });
  }
}

function* moveDid(data) {
  const { groupId, targetGroupId, amount, toggle, setSubmitting, filters } = data;
  setSubmitting(true);
  try {
    const res = yield DidServices.moveDid({
      amount,
      targetGroupId: Number(targetGroupId),
      groupId: Number(groupId),
    });

    if (isResponseSuccess(res.status)) {
      yield getDidDetail({ id: groupId, filters });
      Notification('success', 'Move successfully!', '', TIME_OUT_CLOSE_TOAST);
      setSubmitting(false);
      toggle();
      yield put({ type: types.REQUEST_MOVE_DID_SUCCESS, groupId });
    }
  } catch (error) {
    setSubmitting(false);
    showError(error);
    yield put({ type: types.REQUEST_MOVE_DID_FAILED, error, groupId });
  }
}

function* getAvailablePhone(data) {
  const {
    groupId,
    countryCode,
    areaCode,
    areaCodeSelectionMode,
    phoneCategory,
    voiceEnabled,
    bulkPurchaseCount,
    toggle,
    setSubmitting,
    toggleRoot,
    callbacks,
  } = data;
  try {
    // const res = yield DidServices.getAvailablePhone({
    // 	groupId,
    // 	countryCode,
    // 	areaCode,
    // 	areaCodeSelectionMode,
    // 	phoneCategory,
    // 	bulkPurchaseCount,
    // });

    // if (isResponseSuccess(res.status)) {
    // }
    let submitData = {
      groupId,
      country: countryCode,
      areaCode,
      areaCodeSelectionMode,
      phoneCategory,
      bulkPurchaseCount,
    };
    if (voiceEnabled != null) submitData.voiceEnabled = voiceEnabled;
    const resBulkPurchase = yield DidServices.bulkPurchase(submitData);

    if (isResponseSuccess(resBulkPurchase.status)) {
      yield put({ type: types.REQUEST_BULK_PURCHASE_SUCCESS });
      Notification(
        'success',
        'System is processing your order. Your DID will be available within few minutes!',
        'Purchase',
        TIME_OUT_CLOSE_TOAST
      );
      callbacks && callbacks.onSuccess();
      setSubmitting(false);
      toggle();
      toggleRoot();
    }
  } catch (error) {
    callbacks && callbacks.onFailed && callbacks.onFailed();
    setSubmitting(false);
    showError(error);
    yield put({ type: types.REQUEST_BULK_PURCHASE_FAILED, error });
  }
}

function* searchDid(data) {
  const { keyword } = data;
  yield put({ type: types.REQUEST_SEARCH_DID_SUCCESS, keyword });
}

function* getPurchaseProcess(data) {
  const { groupId } = data;
  const isDidDetailMounted = yield select(
    (state) => state.stateComponent[typeMounted(components.didsDetail)]
  );
  try {
    if (!isOnlyNumber(groupId)) {
      yield put({
        type: types.REQUEST_DID_DETAIL_FAILED,
        error: i18n.t('dids:group_did_is_not_existed'),
      });
      return;
    }

    const res = yield DidServices.getPurchaseProcess(groupId);
    if (isResponseSuccess(res.status) && isDidDetailMounted) {
      yield put({
        type: types.REQUEST_GET_PURCHASE_PROCESS_SUCCESS,
        data: res.data || {},
      });
      if (res.data) {
        yield delay(2000);
        yield getPurchaseProcess({ groupId });
      }
    }
  } catch (error) {
    showError(error);
    yield put({ type: types.REQUEST_GET_PURCHASE_PROCESS_FAILED, error });
  }
}

function* checkPurchaseInAllGroup(payload) {
  const { listGroupIsPurchasing: listDidGroupIsPurchasing } = payload;
  const isDidGroupMounted = yield select(
    (state) => state.stateComponent[typeMounted(components.dids)]
  );
  const listDidGroup = yield select((state) => state.didsReducer.didsGroup.data);

  /**
   * Check all Did Group first
   * -> get & check id groups which purchasing
   */
  const listDidGroupId = listDidGroupIsPurchasing || listDidGroup.map((el) => el.id);
  const listRequest = listDidGroupId.map((id) => DidServices.getPurchaseProcess(id));
  try {
    if (isDidGroupMounted) {
      const res = yield Promise.all(listRequest);
      const listGroupIsPurchasing = res
        // group which have data is purchasing
        .filter((el) => el.data)
        // get list id
        .map((el) => el.data.groupId);

      yield put({
        type: types.REQUEST_CHECK_PURCHASE_ALL_GROUP_SUCCESS,
        listGroupIsPurchasing,
      });

      if (listGroupIsPurchasing.length > 0) {
        yield delay(2000);
        yield checkPurchaseInAllGroup({ listGroupIsPurchasing });
      }
    }
  } catch (error) {
    showError(error);
    yield put({ type: types.REQUEST_CHECK_PURCHASE_ALL_GROUP_FAILED, error });
  }
}

function* deleteSingleDidGroup(data) {
  const { dids, toggle, groupDidId, filters } = data;
  try {
    yield Promise.all(dids.map((didNumber) => DidServices.deleteSingleDidGroup(didNumber)));
    yield getDidDetail({ id: groupDidId, filters });
    Notification('success', 'Delete successfully!', 'Success', TIME_OUT_CLOSE_TOAST);
    toggle();
  } catch (error) {
    showError(error);
    yield put({ type: types.REQUEST_DELETE_SINGLE_DID_FAILED, error });
  }
}

function* moveDidSelected(data) {
  const { targetGroupId, groupId, dids, filters, propsFormik, toggle, clearChecked } = data;
  try {
    const res = yield DidServices.moveDidSelected({
      targetGroupId: targetGroupId.id,
      groupId,
      dids,
    });

    if (isResponseSuccess(res.status)) {
      yield getDidDetail({ id: groupId, filters });
      Notification('success', 'Move DID successfully!', '', TIME_OUT_CLOSE_TOAST);

      propsFormik && propsFormik.setSubmitting(false);
      clearChecked && clearChecked();

      toggle();
      yield put({ type: types.REQUEST_MOVE_DID_SELECTED_SUCCESS, groupId });
    }
  } catch (error) {
    showError(error);
    propsFormik && propsFormik.setSubmitting(false);
    yield put({ type: types.REQUEST_MOVE_DID_SELECTED_FAILED });
  }
}

function* releaseDidNow(data) {
  const { groupId, callbacks } = data;
  try {
    yield DidServices.releaseDidNow(groupId);
    yield put({ type: types.REQUEST_RELEASE_DID_NOW_SUCCESS });
    callbacks && callbacks.onSuccess();
    notifySuccess('Release DID successfully!');
  } catch (error) {
    showError(error);
    callbacks && callbacks.onFailed();
    yield put({ type: types.REQUEST_RELEASE_DID_NOW_FAILED });
  }
}

function* saveDid(data) {
  const { groupId, settings, callbacks } = data;
  try {
    const res = yield DidServices.saveDid({
      groupId,
      body: settings,
    });
    callbacks && callbacks.onSuccess();
    yield put({
      type: types.REQUEST_SAVE_DID_SUCCESS,
      setting: res.data.setting,
    });
    notifySuccess('Update successfully!');
  } catch (error) {
    callbacks && callbacks.onFailed(error);
    yield put({ type: types.REQUEST_SAVE_DID_FAILED, error });
    showError(error);
  }
}

function* getTotalDidInAccount(data) {
  const { callbacks, filters } = data;
  try {
    const res = yield DidServices.getTotalDidInAccount(filters);
    if (isResponseSuccess(res?.status)) {
      yield put({
        type: types.REQUEST_GET_TOTAL_DID_IN_ACCOUNT_SUCCESS,
        total: res?.data || 0,
      });
      callbacks && callbacks.onSuccess(res);
    }
  } catch (error) {
    callbacks && callbacks.onFailed();
    yield put({ type: types.REQUEST_GET_TOTAL_DID_IN_ACCOUNT_FAILED, error });
    showError(error);
  }
}

function* releaseDidByClickRate(data) {
  const { callbacks, groupId, clickRate } = data;
  try {
    const response = yield DidServices.releaseDidNowByClickRate({
      groupId,
      clickRate,
    });
    if (isResponseSuccess(response?.status)) {
      notifySuccess('Release did by click rate successfully!');
      yield put({ type: types.REQUEST_RELEASE_DID_BY_CLICK_RATE_SUCCESS });
      callbacks && callbacks.onSuccess(response);
      return;
    }

    //* Error
    yield put({ type: types.REQUEST_RELEASE_DID_BY_CLICK_RATE_FAILED, error: 'Error' });
    showError('Error');
    callbacks && callbacks.onFailed('error');
  } catch (error) {
    yield put({ type: types.REQUEST_RELEASE_DID_BY_CLICK_RATE_FAILED, error });
    showError(error);
    callbacks && callbacks.onFailed(error);
  }
}

export default function* () {
  yield takeLatest(types.REQUEST_LIST_DIDS, getListDidGroup);
  yield takeLatest(types.REQUEST_ADD_DID_GROUP, createDidGroup);
  yield takeEvery(types.REQUEST_DELETE_DID, deleteDidGroup);
  yield takeLatest(types.REQUEST_DID_DETAIL, getDidDetail);
  yield takeLatest(types.REQUEST_MOVE_DID, moveDid);
  yield takeEvery(types.REQUEST_BULK_PURCHASE, getAvailablePhone);
  yield takeLatest(types.REQUEST_SEARCH_DID, searchDid);
  yield takeLatest(types.REQUEST_GET_PURCHASE_PROCESS, getPurchaseProcess);
  yield takeLatest(types.REQUEST_CHECK_PURCHASE_ALL_GROUP, checkPurchaseInAllGroup);
  yield takeLatest(types.REQUEST_LOAD_MORE_DIDS_GROUP, loadMoreDidsGroup);
  yield takeLatest(types.REQUEST_DELETE_SINGLE_DID, deleteSingleDidGroup);
  yield takeLatest(types.REQUEST_MOVE_DID_SELECTED, moveDidSelected);
  yield takeEvery(types.REQUEST_RELEASE_DID_NOW, releaseDidNow);
  yield takeLatest(types.REQUEST_SAVE_DID, saveDid);
  yield takeLatest(types.REQUEST_GET_TOTAL_DID_IN_ACCOUNT, getTotalDidInAccount);
  yield takeLatest(types.REQUEST_RELEASE_DID_BY_CLICK_RATE, releaseDidByClickRate);
}
